import { useMemo, useState } from "react";
import { addMonths, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  computeAvailableFrom,
  deriveAvailableMonths,
  fromIso,
  isBlocked,
  isWithinWindow,
  monthKey,
  type AvailabilitySource,
} from "@/lib/availability";

// Read-only availability for a listing (renter listing page, admin review,
// owner preview). Full-width months stacked on phones, two side by side on
// desktop; a month-jump strip for medium-term planning; and a legend that
// only advertises states the grid actually renders.
//
// One kind of unavailable day: blocked, in red (Felipe, Oct 27). The old
// "available from / until" window became blocked days; a day outside a window
// still on an old listing shows as blocked too.

/** How a blocked day looks in every calendar (listing page, move-in picker, owner editor). */
export const BLOCKED_DAY_CLASS = "!bg-destructive/15 !text-destructive !font-semibold line-through";

/** A day nobody can rent: blocked by the owner, or outside an old window. */
export const isUnavailableDay = (source: AvailabilitySource, d: Date): boolean => isBlocked(source, d) || !isWithinWindow(source, d);

export const AvailabilityLegend = ({ className }: { className?: string }) => (
  <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground", className)}>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm border border-border bg-card" /> Available
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm bg-destructive/15 border border-destructive/40" /> Blocked
    </span>
  </div>
);

export const MonthJumpStrip = ({
  source,
  month,
  onJump,
  count = 12,
  today = startOfDay(new Date()),
}: {
  source: AvailabilitySource;
  month: Date;
  onJump: (m: Date) => void;
  count?: number;
  today?: Date;
}) => {
  const available = useMemo(() => new Set(deriveAvailableMonths(source, today, count)), [source, today, count]);
  const first = startOfMonth(today);
  const months = Array.from({ length: count }, (_, i) => addMonths(first, i));
  const activeKey = monthKey(month);
  return (
    <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 snap-x" role="tablist" aria-label="Jump to month">
      {months.map((m) => {
        const key = monthKey(m);
        const ok = available.has(key);
        const active = key === activeKey;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onJump(m)}
            className={cn(
              "snap-start shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 min-h-[36px] text-xs font-medium transition-colors",
              active ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card text-foreground hover:border-primary/40",
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", ok ? "bg-success" : "bg-muted-foreground/40")} />
            {format(m, m.getMonth() === 0 || key === monthKey(first) ? "MMM yy" : "MMM")}
          </button>
        );
      })}
    </div>
  );
};

export const AvailabilityCalendar = ({
  source,
  className,
  initialMonth,
  showHeader = true,
  showJumpStrip = true,
}: {
  source: AvailabilitySource;
  className?: string;
  /** e.g. the renter's preferred move-in month. */
  initialMonth?: Date | null;
  showHeader?: boolean;
  showJumpStrip?: boolean;
}) => {
  const isMobile = useIsMobile();
  const today = useMemo(() => startOfDay(new Date()), []);
  const availableFrom = useMemo(() => computeAvailableFrom(source, today), [source, today]);
  const startMonth = startOfMonth(today);
  const [month, setMonth] = useState<Date>(() => {
    const preferred = initialMonth && !isBefore(initialMonth, startMonth) ? startOfMonth(initialMonth) : null;
    return preferred ?? (availableFrom ? startOfMonth(availableFrom) : startMonth);
  });

  return (
    <div className={cn("space-y-3", className)} data-testid="availability-calendar">
      {showHeader && (
        <p className="text-sm">
          {availableFrom ? (
            <>
              <span className="font-medium">Available from {format(availableFrom, "MMM d, yyyy")}</span>
            </>
          ) : (
            <span className="font-medium text-muted-foreground">No availability in the next 18 months</span>
          )}
          <span className="text-muted-foreground"> · Min stay {source.minRentalMonths} month{source.minRentalMonths === 1 ? "" : "s"}</span>
        </p>
      )}
      {showJumpStrip && <MonthJumpStrip source={source} month={month} onJump={setMonth} today={today} />}
      <div className="rounded-xl border bg-card">
        <Calendar
          month={month}
          onMonthChange={setMonth}
          fromMonth={startMonth}
          numberOfMonths={isMobile ? 2 : 2}
          showOutsideDays={false}
          disabled={(d) => isBefore(d, today)}
          modifiers={{ blocked: (d) => !isBefore(d, today) && isUnavailableDay(source, d) }}
          modifiersClassNames={{ blocked: BLOCKED_DAY_CLASS }}
          className="p-3 pointer-events-auto"
        />
      </div>
      <AvailabilityLegend />
    </div>
  );
};

export default AvailabilityCalendar;
